<script setup lang="ts">
import type { CampDetails } from '#shared/types/camps'
import type { Database } from '#shared/types/database.types'
import { computed, ref } from 'vue'
import WaitlistDialog from '@/components/dialogs/WaitlistDialog.vue'
import { useCartStore } from '@/stores/cart'
import { useGodmodeSlots } from '~/composables/useGodmodeSlots'
import { buildAvailabilitySummary, getCapacityColor, getCapacityPercentage } from '~/composables/useTicketAvailability'
import { logApiError } from '~/utils/logger'

// Define a simplified interface for ticket type data to avoid deep type instantiation
interface TicketTypeData {
  id: string
  name: string
  description: string | null
  price: number
  type: 'week' | 'day'
  available_quantity: number | null
  max_quantity: number | null
  sale_start_date: string | null
  sale_end_date: string | null
  available_dates: unknown
  is_active: boolean
}

interface AvailabilityDay {
  date: string
  available: number
  capacity: number
  weekTicketsSold: number
  dayTicketsSold: number
}

interface AvailabilityWeekTicket {
  ticket_type_id: string
  name: string
  available: number
  capacity: number
  weekTicketsSold: number
}

interface AvailabilityData {
  campId: string
  maxAttendees: number
  weekTicketsSold: number
  weekTicketAvailability: AvailabilityWeekTicket[]
  dayAvailability: AvailabilityDay[]
}

type CampStageCamp = Database['public']['Tables']['vzw_camps']['Row']
type CampStageTicketType = Database['public']['Tables']['vzw_ticket_types']['Row']

definePageMeta({
  layout: 'front-page',
  public: true,
})

const route = useRoute()
const campId = ref('')

// This page is also rendered by /kampen/[slug], so `slug` exists although this route's typed params do not list it
const routeParams = route.params as Record<string, string | string[] | undefined>
const slugParam = typeof routeParams.slug === 'string' ? routeParams.slug : ''

if (typeof route.query.id === 'string') {
  campId.value = route.query.id
}
else if (slugParam) {
  const { data: slugData, error: slugError } = await useApi<{ id: string }>(`/api/camps/resolve-slug?slug=${encodeURIComponent(slugParam)}`)

  if (!slugError.value && slugData.value?.id)
    campId.value = slugData.value.id
}

if (!campId.value)
  navigateTo('/')

const campPath = computed(() => (slugParam ? `/kampen/${slugParam}` : `/camp-details?id=${campId.value}`))

const campDetails = ref<CampDetails>()
const { getAgeGroups } = useAgeGroups()
const ageGroupsData = ref<Array<{ id: string, name: string, sort_order: number | null }>>([])

const { canManageCamps, canAccessAdmin, canManageFinance, hasAnyRole } = usePermissions()

const canViewAvailabilityCounts = computed(() => {
  if (canManageCamps.value || canAccessAdmin.value || canManageFinance.value)
    return true

  return hasAnyRole(['admin', 'godmode', 'monitor', 'accountant', 'manager'])
})

const canShowAvailabilityCounts = computed(() => {
  return Boolean(campDetails.value?.showAvailableTickets) || canViewAvailabilityCounts.value
})

// SEO Configuration (will be set after camp data loads)
const { setCampSeo, getBreadcrumbSchema } = useSeo()

// Set the camp specific SEO. This must run during setup, right after the camp data has loaded (see below):
// crawlers and social scrapers only see the server-rendered head, and nuxt-og-image requires
// defineOgImage() to be called within setup. A watcher does neither (SSR never re-runs it and on the
// client it fires after setup).
const applyCampSeo = (newVal: CampDetails) => {
  // Set dynamic SEO for this camp
  const eventSchema = setCampSeo({
    name: newVal.title,
    description: newVal.description || `Ontdek ${newVal.title} bij 't SpeelFabrik`,
    id: campId.value,
    startDate: newVal.startDate,
    endDate: newVal.endDate,
    location: newVal.location,
    price: newVal.price,
    image: newVal.image || undefined,
  })

  // Set structured data for the event
  useSchemaOrg([
    eventSchema,
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Kampen', path: '/kampen' },
      { name: newVal.title, path: campPath.value },
    ]),
  ])

  // Set branded OG image with camp title
  defineOgImage('SpeelFabrik', {
    title: newVal.title,
    description: newVal.period ? `${newVal.period} • ${newVal.location || ''}` : newVal.location || '',
  })
}

// Authentication
const authStore = useAuthStore()
const isAuthenticated = computed(() => authStore.isAuthenticated)

// Pre-registration status
const isAlreadyPreRegistered = ref(false)
const userEmail = computed(() => authStore.user?.email || '')

// Waitlist dialog
const showWaitlistDialog = ref(false)
const { getPrimaryAction } = useCampStages()

// Snackbar state
const snackbar = ref({
  visible: false,
  message: '',
  color: 'success',
})

const showSnackbar = (message: string, color: string = 'success') => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.visible = true
}

const { data, error } = await useApi<CampDetails>(`/api/camps/camp-details?id=${campId.value}`)

if (error.value) {
  navigateTo('/')
}
else if (data.value) {
  campDetails.value = data.value
  applyCampSeo(data.value)
}

// Load real ticket types
const { getPublicTicketTypes } = useTicketTypes()
const ticketTypesData = ref<TicketTypeData[]>([])

// Start as true to prevent flash of incorrect content during SSR hydration
const loadingTickets = ref(true)
const availabilityData = ref<AvailabilityData | null>(null)

const availabilitySummary = computed(() => {
  if (!availabilityData.value)
    return null

  return buildAvailabilitySummary(availabilityData.value)
})

const loadAvailability = async () => {
  try {
    const response = await $fetch<{ success: boolean, data: AvailabilityData }>(`/api/camps/${campId.value}/availability`)
    if (response.success)
      availabilityData.value = response.data
  }
  catch (err) {
    logApiError(`/api/camps/${campId.value}/availability`, err)
  }
}

const checkPreRegistrationStatus = async () => {
  try {
    const response = await $fetch(`/api/camps/${campId.value}/pre-register-status`)

    isAlreadyPreRegistered.value = response.isRegistered
  }
  catch (err) {
    logApiError(`/api/camps/${campId.value}/pre-register-status`, err)
  }
}

onMounted(async () => {
  try {
    const ageGroupResponse = await getAgeGroups(true)

    ageGroupsData.value = (ageGroupResponse.data || []) as Array<{ id: string, name: string, sort_order: number | null }>

    const response = await getPublicTicketTypes(campId.value)
    if (response.data)
      ticketTypesData.value = response.data

    // Load real-time availability
    await loadAvailability()

    // Check pre-registration status
    await checkPreRegistrationStatus()
  }
  catch (err) {
    logApiError('/api/ticket-types', err, { campId: campId.value })
  }
  finally {
    loadingTickets.value = false
  }
})

// Watch for authentication changes and recheck pre-registration status
watch(isAuthenticated, async () => {
  await checkPreRegistrationStatus()
})

const resolveChipColor = (category: string) => {
  const colors: Record<string, string> = {
    'Sport': 'primary',
    'Kunst & Knutsel': 'success',
    'Avontuur': 'error',
    'Wetenschap': 'info',
    'Muziek': 'warning',
    'Natuur': 'success',
    'Technologie': 'secondary',
  }

  return colors[category] || 'primary'
}

const sortedAgeGroups = computed(() => {
  return [...ageGroupsData.value].sort((a, b) => {
    const aOrder = a.sort_order ?? Number.POSITIVE_INFINITY
    const bOrder = b.sort_order ?? Number.POSITIVE_INFINITY

    return aOrder - bOrder
  })
})

const campAgeLabel = computed(() => {
  if (!campDetails.value)
    return ''

  const ageGroupIds = campDetails.value.ageGroupIds || []
  if (ageGroupIds.length === 0 || sortedAgeGroups.value.length === 0)
    return campDetails.value.ageGroup || 'Niet opgegeven'

  const selectedGroups = sortedAgeGroups.value.filter(group => ageGroupIds.includes(group.id))

  if (selectedGroups.length === 0)
    return campDetails.value.ageGroup || 'Niet opgegeven'

  if (selectedGroups.length === 1)
    return selectedGroups.at(0)?.name || (campDetails.value.ageGroup || 'Niet opgegeven')

  const first = selectedGroups.at(0)?.name
  const last = selectedGroups.at(-1)?.name

  if (!first || !last)
    return campDetails.value.ageGroup || 'Niet opgegeven'

  return `${first} tot en met ${last}`
})

// Check if camp has public categories to display
const hasPublicCategory = computed(() => {
  if (!campDetails.value)
    return false

  // Hide if category is 'Algemeen' (default when no public categories exist)
  if (campDetails.value.category === 'Algemeen')
    return false

  // Hide if categories array exists but is empty
  if (campDetails.value.categories && campDetails.value.categories.length === 0)
    return false

  // Show if there's a valid category
  return campDetails.value.category && campDetails.value.category.trim() !== ''
})

// Imported from ~/utils/dates

// Booking
const cartStore = useCartStore()
const { resetSlots } = useGodmodeSlots()

const campInfo = computed(() => {
  if (!campDetails.value)
    return null

  return {
    id: campId.value,
    title: campDetails.value.title,
    location: campDetails.value.location,
    ageGroup: campDetails.value.ageGroup,
    startDate: campDetails.value.startDate,
    endDate: campDetails.value.endDate,
    period: campDetails.value.period,
    refundPolicy: campDetails.value.refundPolicy,
    enableInvoice: campDetails.value.enableInvoice,
    paymentMethods: campDetails.value.paymentMethods,
    showAvailableTickets: campDetails.value.showAvailableTickets,
  }
})

// Dialog states
const isTicketInfoDialogOpen = ref(false)

// Pre-registration Dialog
const isPreRegDialogOpen = ref(false)
const preRegEmail = ref('')
const isPreRegSubmitting = ref(false)

// Check if ticket sales have started by examining actual loaded tickets
const ticketSalesStarted = computed(() => {
  // If no tickets loaded yet, default to false
  if (!ticketTypesData.value || ticketTypesData.value.length === 0)
    return false

  const now = new Date()

  // Check if ANY ticket has sales period that has started
  return ticketTypesData.value.some(ticket => {
    // Inactive tickets don't count
    if (!ticket.is_active)
      return false

    // If no sale_start_date set, ticket is available
    if (!ticket.sale_start_date)
      return true

    const saleStartDate = new Date(ticket.sale_start_date)

    // Check if sale has started
    if (now < saleStartDate)
      return false

    // Check if sale hasn't ended (if sale_end_date is set)
    if (ticket.sale_end_date) {
      const saleEndDate = new Date(ticket.sale_end_date)
      if (now > saleEndDate)
        return false
    }

    return true
  })
})

// True when a sale period existed before but has now closed (sale_start_date is past, no active tickets)
const ticketSaleClosed = computed(() => {
  if (ticketSalesStarted.value)
    return false

  const saleStartStr = campDetails.value?.ticketSaleStartDate
  if (!saleStartStr)
    return false

  return new Date() >= new Date(saleStartStr)
})

// Get primary action using centralized stage logic
const primaryCTA = computed(() => {
  if (!campDetails.value)
    return null

  // Convert campDetails to format expected by getPrimaryAction
  const campData = {
    id: campId.value,
    status: 'published',
    start_date: campDetails.value.startDate,
    end_date: campDetails.value.endDate,
    registration_start_date: campDetails.value.preRegistration?.startDate,
    ticket_sale_start_date: campDetails.value.ticketSaleStartDate,
    enable_waitlist: campDetails.value.enable_waitlist || false,
    waitlist_capacity: campDetails.value.waitlist_capacity || null,
    max_attendees: campDetails.value.max_attendees || null,
    current_attendees: campDetails.value.current_attendees || null,
  } as CampStageCamp

  const baseAction = getPrimaryAction(campData, ticketTypesData.value as CampStageTicketType[])

  // Only show book button for limited availability when ticket sales are actually open
  if (ticketSalesStarted.value && availabilitySummary.value?.limited) {
    return {
      label: 'Boek nu',
      text: 'Boek nu',
      action: 'book',
      icon: 'bx-cart',
      color: 'primary',
    }
  }

  return baseAction
})

const primaryCTADisabled = computed(() => {
  const action = primaryCTA.value

  if (!action)
    return false

  return 'disabled' in action ? (action.disabled ?? false) : false
})

const openBookingDialog = async () => {
  if (!campInfo.value)
    return

  const info = campInfo.value
  const ageGroup = info.ageGroup?.split('-')
  const campId = String(info.id)

  cartStore.initCart(campId, info.title, {
    location: info.location,
    age_min: ageGroup?.[0] ? Number.parseInt(ageGroup[0].trim()) : undefined,
    age_max: ageGroup?.[1] ? Number.parseInt(ageGroup[1].trim()) : undefined,
    start_date: info.startDate,
    end_date: info.endDate,
    period: info.period,
    refund_policy: info.refundPolicy,
    enable_invoice: info.enableInvoice,
    payment_methods: info.paymentMethods,
  })
  resetSlots()
  cartStore.saveToLocalStorage()

  try {
    await navigateTo('/cart')
  }
  catch (error) {
    logApiError('navigateTo:/cart', error)
    window.location.href = '/cart'
  }
}

const shareCamp = async () => {
  const shareUrl = window.location.origin + campPath.value

  const shareData = {
    title: campDetails.value?.title || 'Kamp',
    text: `Bekijk ${campDetails.value?.title} bij 't SpeelFabrik!`,
    url: shareUrl,
  }

  try {
    // Use native share API if available (mobile/modern browsers)
    if (navigator.share) {
      await navigator.share(shareData)
    }
    else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar('Link gekopieerd naar klembord!', 'success')
    }
  }
  catch {
    // User cancelled or error - try clipboard as fallback
    try {
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar('Link gekopieerd naar klembord!', 'success')
    }
    catch {
      showSnackbar('Kon link niet kopiëren', 'error')
    }
  }
}

const handleWaitlistJoined = () => {
  showSnackbar('Je bent succesvol toegevoegd aan de wachtlijst!', 'success')

  // Refresh availability data
  loadAvailability()
}

const openPreRegDialog = () => {
  preRegEmail.value = ''
  isPreRegDialogOpen.value = true
}

const closePreRegDialog = () => {
  isPreRegDialogOpen.value = false
  preRegEmail.value = ''
}

const submitPreRegistration = async () => {
  const emailToUse = isAuthenticated.value ? userEmail.value : preRegEmail.value

  if (!emailToUse || !/^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(emailToUse)) {
    showSnackbar('Voer een geldig e-mailadres in', 'error')

    return
  }

  isPreRegSubmitting.value = true
  try {
    const response = await $fetch(`/api/camps/${campId.value}/pre-register`, {
      method: 'POST',
      body: { email: emailToUse },
    })

    if (response.success) {
      showSnackbar(response.message, response.alreadyRegistered ? 'info' : 'success')
      isAlreadyPreRegistered.value = true
      closePreRegDialog()
    }
  }
  catch (err) {
    logApiError(`/api/camps/${campId.value}/pre-register`, err)

    const error = err as { data?: { statusMessage?: string } }

    showSnackbar(error.data?.statusMessage || 'Er is een fout opgetreden', 'error')
  }
  finally {
    isPreRegSubmitting.value = false
  }
}
</script>

<template>
  <div class="camp-details-page">
    <VContainer>
      <VRow v-if="campDetails">
        <VCol cols="12">
          <div class="camp-mobile-title text-center">
            <h1 class="camp-mobile-title__title">
              {{ campDetails.title }}
            </h1>
            <p class="camp-mobile-title__subtitle">
              {{ campDetails.period }} • {{ campDetails.location }}
            </p>
          </div>
        </VCol>
        <VCol cols="12" md="8" order="2" order-md="1">
          <VCard>
            <VCardText class="pa-0">
              <VCard flat border>
                <!-- Image with overlay buttons -->
                <div class="position-relative d-none d-md-block">
                  <VImg
                    v-if="campDetails.image"
                    :src="campDetails.image || ''"
                    :height="$vuetify.display.mdAndUp ? 400 : 280"
                    cover
                  />
                  <div
                    v-else
                    class="camp-hero-placeholder d-flex align-center justify-center"
                    :style="{ height: $vuetify.display.mdAndUp ? '400px' : '280px' }"
                  />
                  <!-- Top left badges -->
                  <div class="position-absolute" style="z-index: 2; inset-block-start: 16px; inset-inline-start: 16px">
                    <div class="d-flex gap-2 flex-wrap">
                      <VChip
                        v-if="hasPublicCategory"
                        variant="elevated"
                        :color="resolveChipColor(campDetails.category)"
                        size="small"
                        class="font-weight-medium"
                      >
                        {{ campDetails.category }}
                      </VChip>
                      <VChip v-if="campDetails.featured" variant="elevated" color="warning" size="small">
                        <VIcon icon="bx-star" size="16" class="me-1" />
                        Voorgesteld
                      </VChip>
                      <VChip v-for="tag in campDetails.tags" :key="tag" variant="elevated" color="default" size="small">
                        {{ tag }}
                      </VChip>
                    </div>
                  </div>
                  <!-- Top right action buttons -->
                  <div class="position-absolute" style="z-index: 2; inset-block-start: 16px; inset-inline-end: 16px">
                    <VBtn icon size="small" variant="elevated" color="white" class="elevation-2" @click="shareCamp">
                      <VIcon icon="bx-share-alt" size="20" />
                      <VTooltip activator="parent" location="bottom">
                        Deel dit kamp
                      </VTooltip>
                    </VBtn>
                  </div>
                </div>

                <VCardText>
                  <!-- Details Section (Rich Text HTML) -->
                  <template v-if="campDetails.details">
                    <h5 class="text-h5 mb-4">
                      Kamp details
                    </h5>
                    <div class="text-body-1 camp-details-content" v-html="sanitizeHtml(campDetails.details)" />
                  </template>
                </VCardText>
              </VCard>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="4" order="1" order-md="2">
          <!-- Camp Info Card -->
          <VCard class="mb-6">
            <VCardText>
              <h5 class="text-h5 mb-4">
                Kamp Informatie
              </h5>

              <VList class="card-list">
                <VListItem class="px-0">
                  <template #prepend>
                    <VIcon icon="bx-calendar" size="20" class="me-2" />
                  </template>
                  <VListItemTitle class="text-body-2 text-medium-emphasis">
                    Periode
                  </VListItemTitle>
                  <VListItemSubtitle class="text-body-1 font-weight-medium">
                    <template v-if="campDetails.periodNames && campDetails.periodNames.length > 0">
                      <VChip
                        v-for="periodName in campDetails.periodNames"
                        :key="periodName"
                        size="small"
                        variant="tonal"
                        color="primary"
                        class="me-1 mb-1"
                      >
                        {{ periodName }}
                      </VChip>
                    </template>
                    <template v-else>
                      {{ campDetails.period }}
                    </template>
                  </VListItemSubtitle>
                </VListItem>

                <VListItem class="px-0">
                  <template #prepend>
                    <VIcon icon="bx-calendar-event" size="20" class="me-2" />
                  </template>
                  <VListItemTitle class="text-body-2 text-medium-emphasis">
                    Datum
                  </VListItemTitle>
                  <VListItemSubtitle class="text-body-1 font-weight-medium">
                    {{ formatDate(campDetails.startDate) }} - {{ formatDate(campDetails.endDate) }}
                  </VListItemSubtitle>
                </VListItem>

                <VListItem class="px-0">
                  <template #prepend>
                    <VIcon icon="bx-time-five" size="20" class="me-2" />
                  </template>
                  <VListItemTitle class="text-body-2 text-medium-emphasis">
                    Duur
                  </VListItemTitle>
                  <VListItemSubtitle class="text-body-1 font-weight-medium">
                    {{ campDetails.duration }}
                  </VListItemSubtitle>
                </VListItem>

                <VListItem class="px-0">
                  <template #prepend>
                    <VIcon icon="bx-user" size="20" class="me-2" />
                  </template>
                  <VListItemTitle class="text-body-2 text-medium-emphasis">
                    Leeftijd
                  </VListItemTitle>
                  <VListItemSubtitle class="text-body-1 font-weight-medium">
                    {{ campAgeLabel }}
                  </VListItemSubtitle>
                </VListItem>

                <VListItem class="px-0">
                  <template #prepend>
                    <VIcon icon="bx-map" size="20" class="me-2" />
                  </template>
                  <VListItemTitle class="text-body-2 text-medium-emphasis">
                    Locatie
                  </VListItemTitle>
                  <VListItemSubtitle class="text-body-1 font-weight-medium">
                    {{ campDetails.location }}
                  </VListItemSubtitle>
                </VListItem>
              </VList>

              <VDivider class="my-4" />

              <!-- Spots Available - only show when ticket sales have started and data is loaded -->
              <template v-if="loadingTickets">
                <VSkeletonLoader type="text" class="mb-4" />
              </template>
              <div v-else-if="ticketSalesStarted" class="mb-4">
                <div v-if="availabilitySummary?.limited" class="text-h6 text-warning font-weight-bold text-center mb-2">
                  Beperkte beschikbaarheid
                </div>
                <div v-else-if="availabilitySummary?.soldOut" class="text-h6 text-error font-weight-bold text-center">
                  Volzet
                </div>
                <template v-else-if="availabilitySummary && availabilitySummary.effectiveRemaining > 0">
                  <template v-if="canShowAvailabilityCounts">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="text-body-2 text-medium-emphasis">Beschikbare plekken</span>
                      <span class="text-body-1 font-weight-medium">{{ availabilitySummary.effectiveRemaining }} / {{ availabilitySummary.maxAttendees }}</span>
                    </div>
                    <VProgressLinear
                      :model-value="getCapacityPercentage(availabilitySummary)"
                      :color="getCapacityColor(availabilitySummary)"
                      height="8"
                      rounded
                    />
                    <div
                      v-if="availabilitySummary.effectiveRemaining <= 10"
                      class="text-caption mt-1"
                      :class="availabilitySummary.effectiveRemaining <= 5 ? 'text-error' : 'text-warning'"
                    >
                      <VIcon :icon="availabilitySummary.effectiveRemaining <= 5 ? 'bx-error-circle' : 'bx-time'" size="14" class="me-1" />
                      {{ availabilitySummary.effectiveRemaining <= 5 ? 'Bijna vol!' : 'Snel vol!' }}
                    </div>
                  </template>
                  <div v-else class="text-h6 font-weight-bold text-center">
                    Beschikbaar
                  </div>
                </template>
                <div v-else class="text-h6 text-error font-weight-bold text-center">
                  Volzet
                </div>
              </div>

              <VDivider v-if="!loadingTickets && ticketSalesStarted" class="my-4" />

              <!-- Pricing Section -->
              <div v-if="loadingTickets" class="mb-4">
                <VSkeletonLoader type="text" />
              </div>
              <div v-else class="mb-4">
                <div v-if="ticketTypesData.length === 1" class="d-flex justify-space-between align-center">
                  <span class="text-body-2 text-medium-emphasis">Prijs per persoon</span>
                  <span
                    class="text-h6 font-weight-bold"
                    :class="ticketTypesData[0]?.price === 0 ? 'text-error' : 'text-primary'"
                  >
                    {{ ticketTypesData[0]?.price === 0 ? 'Volzet' : `€${ticketTypesData[0]?.price}` }}
                  </span>
                </div>
                <div v-else-if="ticketTypesData.length > 1">
                  <div class="text-body-2 text-medium-emphasis mb-3">
                    Kostprijs
                  </div>
                  <VList class="pa-0">
                    <VListItem v-for="ticket in ticketTypesData" :key="ticket.id" class="px-0 py-2">
                      <VListItemTitle class="d-flex justify-space-between align-center">
                        <span class="text-body-1">{{ ticket.name }}</span>
                        <span :class="ticket.price === 0 ? 'text-error' : 'text-primary'">
                          {{ ticket.price === 0 ? 'Volzet' : `€${ticket.price}`
                          }}{{ ticket.price === 0 ? '' : ticket.type === 'day' ? ' / dag' : '' }}
                        </span>
                      </VListItemTitle>
                    </VListItem>
                  </VList>
                </div>
                <div v-else>
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-body-2 text-medium-emphasis">Prijs per persoon</span>
                    <span
                      class="text-h6 font-weight-bold"
                      :class="campDetails.price === 0 ? 'text-error' : 'text-primary'"
                    >
                      {{ campDetails.price === 0 ? 'Volzet' : `€${campDetails.price}` }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="ticketSalesStarted && !availabilitySummary?.limited && campDetails.spotsLeft > 0 && campDetails.spotsLeft <= 10"
                class="text-warning mb-4 text-center"
              >
                <VIcon icon="bx-error" />
                <span v-if="canShowAvailabilityCounts">Nog maar {{ campDetails.spotsLeft }} plekken beschikbaar!</span>
                <span v-else>Nog maar enkele plekken beschikbaar!</span>
              </div>

              <div class="text-center">
                <!-- Loading skeleton for CTA -->
                <template v-if="loadingTickets">
                  <VSkeletonLoader type="button" class="mb-3" />
                </template>

                <!-- Dynamic CTA based on camp stage -->
                <template v-else-if="primaryCTA">
                  <!-- Book Now Button -->
                  <VBtn
                    v-if="primaryCTA.action === 'book'"
                    :color="primaryCTA.color"
                    size="large"
                    block
                    class="mb-3"
                    prepend-icon="bx-cart"
                    :disabled="primaryCTADisabled"
                    @click="openBookingDialog"
                  >
                    {{ primaryCTA.text }}
                  </VBtn>

                  <!-- Pre-registration Button -->
                  <VBtn
                    v-else-if="primaryCTA.action === 'pre-register'"
                    :color="isAlreadyPreRegistered ? 'success' : primaryCTA.color"
                    size="large"
                    block
                    class="mb-3"
                    :prepend-icon="isAlreadyPreRegistered ? 'bx-check-circle' : 'bx-bell'"
                    :disabled="isAlreadyPreRegistered || primaryCTADisabled"
                    @click="openPreRegDialog"
                  >
                    {{ isAlreadyPreRegistered ? 'Je wordt op de hoogte gehouden' : primaryCTA.text }}
                  </VBtn>

                  <!-- Waitlist Button -->
                  <VBtn
                    v-else-if="primaryCTA.action === 'waitlist'"
                    :color="primaryCTA.color"
                    size="large"
                    block
                    class="mb-3"
                    prepend-icon="bx-time"
                    :disabled="primaryCTADisabled"
                    @click="showWaitlistDialog = true"
                  >
                    {{ primaryCTA.text }}
                  </VBtn>

                  <!-- Coming Soon - Ticket sales not started -->
                  <VBtn
                    v-else-if="primaryCTA.action === 'coming-soon'"
                    :color="primaryCTA.color"
                    size="large"
                    block
                    class="mb-3"
                    prepend-icon="bx-calendar-event"
                    disabled
                  >
                    {{ primaryCTA.label }}
                  </VBtn>

                  <!-- Sold Out / No Action -->
                  <VBtn v-else color="secondary" size="large" block class="mb-3" disabled>
                    {{ primaryCTA.text || 'Binnenkort beschikbaar' }}
                  </VBtn>

                  <!-- Secondary Waitlist Button - show when limited availability and waitlist enabled -->
                  <VBtn
                    v-if="availabilitySummary?.limited && campDetails?.enable_waitlist"
                    color="warning"
                    variant="outlined"
                    size="large"
                    block
                    prepend-icon="bx-time"
                    @click="showWaitlistDialog = true"
                  >
                    Schrijf in voor wachtlijst
                  </VBtn>
                </template>

                <!-- Fallback if no CTA computed (and not loading) -->
                <VBtn v-else-if="!loadingTickets" color="secondary" size="large" block class="mb-3" disabled>
                  {{ ticketSaleClosed ? 'Ticket verkoop gesloten' : 'Binnenkort beschikbaar' }}
                </VBtn>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Ticket Info Dialog -->
    <VDialog v-model="isTicketInfoDialogOpen" max-width="600">
      <VCard>
        <VCardTitle class="d-flex justify-space-between align-center">
          <span class="text-h5">Ticket Informatie</span>
          <VBtn icon variant="text" @click="isTicketInfoDialogOpen = false">
            <VIcon icon="bx-x" />
          </VBtn>
        </VCardTitle>

        <VCardText>
          <!-- Info Message -->
          <VAlert type="info" variant="tonal" class="mb-4">
            <div class="text-body-2">
              <div class="font-weight-medium mb-2">
                Ticket Combinaties
              </div>
              Je kunt week tickets en dag tickets combineren. Bijvoorbeeld: 1 kind voor de hele week, en een ander kind
              alleen op maandag en woensdag.
            </div>
          </VAlert>

          <!-- Capacity Warning -->
          <VAlert v-if="availabilityData && canShowAvailabilityCounts" type="warning" variant="tonal">
            <div class="text-body-2">
              <div class="font-weight-medium mb-2">
                Capaciteit overzicht:
              </div>
              <div>Maximum deelnemers: {{ availabilityData.maxAttendees }}</div>
              <div>Week tickets verkocht: {{ availabilityData.weekTicketsSold }}</div>
              <div>
                Resterende week ticket plekken:
                {{ Math.max(0, availabilityData.maxAttendees - availabilityData.weekTicketsSold) }}
              </div>
              <div class="text-caption mt-2">
                Let op: Dag tickets tellen mee bij de totale capaciteit per dag. Als een dag vol is met week tickets,
                kunnen er geen dag tickets meer worden verkocht voor die dag.
              </div>
            </div>
          </VAlert>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn color="primary" @click="isTicketInfoDialogOpen = false">
            Begrepen
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Pre-registration Dialog -->
    <VDialog v-model="isPreRegDialogOpen" max-width="500" persistent>
      <VCard>
        <VCardTitle class="d-flex justify-space-between align-center">
          <span class="text-h5">Hou me op de hoogte</span>
          <VBtn icon variant="text" @click="closePreRegDialog">
            <VIcon icon="bx-x" />
          </VBtn>
        </VCardTitle>

        <VCardText>
          <!-- For authenticated users -->
          <template v-if="isAuthenticated">
            <VAlert variant="outlined" class="mb-4">
              <div class="text-body-2">
                We sturen je een e-mail op <strong>{{ userEmail }}</strong> zodra de ticketverkoop start voor
                <strong>{{ campDetails?.title }}</strong>.
              </div>
            </VAlert>
          </template>

          <!-- For non-authenticated users -->
          <template v-else>
            <VAlert type="info" variant="tonal" class="mb-4">
              <div class="text-body-2">
                Vul je e-mailadres in en ontvang een notificatie zodra de ticketverkoop start voor
                <strong>{{ campDetails?.title }}</strong>.
              </div>
            </VAlert>

            <VTextField
              v-model="preRegEmail"
              label="E-mailadres"
              type="email"
              prepend-inner-icon="bx-envelope"
              variant="outlined"
              :rules="[
                (v: string) => !!v || 'E-mailadres is verplicht',
                (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Voer een geldig e-mailadres in',
              ]"
            />
          </template>
        </VCardText>

        <VCardActions class="px-6 pb-6">
          <VBtn variant="outlined" :disabled="isPreRegSubmitting" @click="closePreRegDialog">
            Annuleren
          </VBtn>
          <VSpacer />
          <VBtn color="primary" prepend-icon="bx-bell" :loading="isPreRegSubmitting" @click="submitPreRegistration">
            {{ isAuthenticated ? 'Bevestig' : 'Schrijf me in' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Snackbar for notifications -->
    <VSnackbar v-model="snackbar.visible" :color="snackbar.color" location="top end" :timeout="3000">
      {{ snackbar.message }}
    </VSnackbar>

    <!-- Waitlist Dialog -->
    <WaitlistDialog
      v-model="showWaitlistDialog"
      :camp-id="campId"
      :camp-name="campDetails?.title || ''"
      :ticket-types="ticketTypesData"
      @joined="handleWaitlistJoined"
    />
  </div>
</template>

<style lang="scss" scoped>
.card-list {
  --v-card-list-gap: 16px;
}

.camp-details-page {
  padding-block-start: 90px;
}

.camp-mobile-title__title {
  margin: 0;
  font-family: 'Peachy Kee JF', 'Public Sans', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.camp-mobile-title__subtitle {
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-family: 'Peachy Kee JF', 'Public Sans', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  margin-block: 6px 0;
  margin-inline: 0;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 960px) {
  .camp-details-page {
    padding-block-start: 84px;
  }
}
</style>

<style lang="scss">
@use '@layouts/styles/mixins' as layoutsMixins;

// Remove default v-list styling (scoped to this page only)
.camp-details-page .v-list {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

// Make dialog responsive - not fullscreen on larger screens
.dialog-not-fullscreen {
  .v-overlay__content {
    inline-size: 100%;
    max-block-size: 90vh;
    max-inline-size: 800px;
  }
}

// Expansion panels styling
body .v-layout .v-application__wrap {
  .expansion-panels-width-border {
    .v-expansion-panels {
      border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
      border-radius: 6px;

      .v-expansion-panel {
        &--active {
          .v-expansion-panel-title--active {
            border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

            .v-expansion-panel-title__overlay {
              opacity: var(--v-hover-opacity) !important;
            }
          }
        }

        .v-expansion-panel-title {
          .v-expansion-panel-title__overlay {
            background-color: rgba(var(--v-theme-on-surface));
            opacity: var(--v-hover-opacity) !important;
          }

          &:hover {
            .v-expansion-panel-title__overlay {
              opacity: var(--v-hover-opacity) !important;
            }
          }

          &__icon {
            .v-icon {
              block-size: 1.5rem !important;
              color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
              font-size: 1.5rem !important;
              inline-size: 1.5rem !important;

              @include layoutsMixins.rtl {
                transform: scaleX(-1);
              }
            }
          }
        }

        .v-expansion-panel-text {
          &__wrapper {
            padding-block: 1rem;
            padding-inline: 0.75rem;
          }
        }
      }
    }
  }

  .card-list {
    .v-list-item__prepend {
      .v-list-item__spacer {
        inline-size: 8px !important;
      }
    }
  }

  .camp-details-content {
    // Styles for rich text content
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: 600;
      margin-block: 1.5em 0.75em;
    }

    h1 {
      font-size: 2em;
    }

    h2 {
      font-size: 1.75em;
    }

    h3 {
      font-size: 1.5em;
    }

    h4 {
      font-size: 1.25em;
    }

    h5 {
      font-size: 1.1em;
    }

    h6 {
      font-size: 1em;
    }

    p {
      line-height: 1.6;
      margin-block-end: 1em;

      &:empty {
        min-block-size: 1.6em;
      }
    }

    ul,
    ol {
      margin-block: 0.5em 1em;
      padding-inline-start: 2em;
    }

    li {
      margin-block-end: 0.5em;
    }

    strong {
      font-weight: 600;
    }

    em {
      font-style: italic;
    }

    mark {
      border-radius: 2px;
      background-color: #ffd54f;
      padding-block: 2px;
      padding-inline: 4px;
    }

    a {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;

      &:hover {
        opacity: 0.8;
      }
    }

    img {
      border-radius: 8px;
      block-size: auto;
      margin-block: 1em;
      max-inline-size: 100%;
    }

    blockquote {
      border-inline-start: 4px solid rgb(var(--v-theme-primary));
      color: rgba(var(--v-theme-on-surface), 0.7);
      font-style: italic;
      margin-block: 1em;
      padding-inline-start: 1em;
    }

    code {
      border-radius: 4px;
      background-color: rgba(var(--v-theme-surface-variant), 0.5);
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.9em;
      padding-block: 2px;
      padding-inline: 6px;
    }

    pre {
      padding: 1em;
      border-radius: 8px;
      background-color: rgba(var(--v-theme-surface-variant), 0.5);
      margin-block: 1em;
      overflow-x: auto;

      code {
        padding: 0;
        background: none;
      }
    }
  }
}

.camp-hero-placeholder {
  background-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.camp-hero-title-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  inset: 0;
  pointer-events: none;
}

.camp-hero-title-overlay__content {
  max-inline-size: 90%;
}

.camp-hero-title-overlay__title {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  -webkit-box-orient: vertical;
  font-family: 'Peachy Kee JF', 'Public Sans', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  line-height: 1.2;
  text-overflow: ellipsis;
}

.camp-hero-title-overlay__subtitle {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  font-family: 'Peachy Kee JF', 'Public Sans', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  line-height: 1.3;
  margin-block: 8px 0;
  margin-inline: 0;
  text-overflow: ellipsis;
}

.camp-hero-title-overlay--image {
  background: rgba(255, 255, 255, 60%);
  color: #000;
}

.camp-hero-title-overlay--placeholder {
  color: #fff;
}
</style>
